import * as React from "react";
import { Check, Command, DraftingCompass, Globe, LifeBuoy, Monitor, Send } from "lucide-react";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarSeparator,
} from "@/components/ui/sidebar";
import { t } from "i18next";
import { Routes } from "@/router/routes";
import { Tenant, useTenants } from "@/api/tenant.api";
import { useSuspenseUserAccess, useUserData } from "@/api/user.api";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocation, useNavigate } from "react-router";
import { useCallback, useMemo } from "react";
import { PermissionDto, Resource } from "@exibra/shared";

const data = {
    navMain: [
        {
            title: t("applications.title"),
            url: Resource.WebsiteApplication,
            icon: Globe,
            enabled: (userAccess: PermissionDto[]) => userAccess.find(p => p.module === Resource.WebsiteApplication)?.read ?? false,
        },
        {
            title: t("templates.title"),
            url: `${Routes.Templates}`,
            icon: DraftingCompass,
            enabled: (userAccess: PermissionDto[]) =>
                userAccess.find((p) => p.module === Resource.Template)?.read ?? false,
        },
        {
            title: t("screens.title"),
            url: `${Routes.Screens}`,
            icon: Monitor,
            enabled: (userAccess: PermissionDto[]) =>
                userAccess.find((p) => p.module === Resource.Screen)?.read ?? false,
        },
    ],
    navSecondary: [
        {
            title: t("users.title"),
            url: Resource.User,
            icon: LifeBuoy,
            enabled: (userAccess: PermissionDto[]) =>
                userAccess.find((p) => p.module === Resource.User)?.read ?? false,
        },
        {
            title: t("billing.title"),
            url: "#",
            icon: Send,
            enabled: (userAccess: PermissionDto[]) =>
                // TODO: Remove this once billing is implemented
                (false && userAccess.find((p) => p.module === Resource.Billing)?.read) ?? false,
        },
    ],
};

/**
 * The props for the AppSidebar component
 */
interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
    /** The tenant to display in the sidebar */
    tenant: Tenant;
}

/**
 * The main sidebar component for the application
 */
export function AppSidebar({ tenant, ...props }: AppSidebarProps) {
    const { data: userData } = useUserData();
    const { data: tenants } = useTenants();
    const hasManyTenants = tenants && tenants.length > 1;
    const location = useLocation();
    const navigate = useNavigate();
    const { data: userAccess } = useSuspenseUserAccess(tenant.id);

    const navItems = useMemo(() => {
        return data.navMain.map((item) => {
            const isActive = location.pathname.includes(item.url);
            return {
                ...item,
                isActive,
                enabled: item.enabled(userAccess ?? []),
            };
        });
    }, [location.pathname, userAccess]);

    const secondaryItems = useMemo(() => {
        return data.navSecondary.map((item) => {
            const isActive = location.pathname.includes(item.url);
            return {
                ...item,
                isActive,
                enabled: item.enabled(userAccess ?? []),
            };
        });
    }, [location.pathname, userAccess]);

    const onTenantChange = useCallback(
        (tenantId: number) => {
            navigate(`/${Routes.Tenant}/${tenantId}`);
        },
        [navigate],
    );

    return (
        <Sidebar variant="inset" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        {hasManyTenants ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-all duration-150 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                                        <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-brand-lime font-heading text-base font-extrabold leading-none tracking-tighter text-brand-ink">
                                            X
                                        </div>
                                        <div className="grid flex-1 text-left text-sm leading-tight">
                                            <span className="truncate font-semibold">
                                                Exibra CMS
                                            </span>
                                            <span className="truncate text-xs text-sidebar-foreground/60">
                                                {tenant?.name}
                                            </span>
                                        </div>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="w-[--radix-dropdown-menu-trigger-width]"
                                >
                                    {tenants?.map((t: Tenant) => (
                                        <DropdownMenuItem
                                            key={t.id}
                                            onClick={() => onTenantChange?.(t.id)}
                                        >
                                            <span className="flex-1">{t.name}</span>
                                            {t.id === tenant?.id && <Check className="size-4" />}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex w-full items-center gap-3 rounded-lg px-3 py-2">
                                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 text-sidebar-primary-foreground">
                                    <Command className="size-4" />
                                </div>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">Exibra CMS</span>
                                    <span className="truncate text-xs text-sidebar-foreground/60">
                                        {tenant?.name}
                                    </span>
                                </div>
                            </div>
                        )}
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarSeparator />
            <SidebarContent>
                <NavMain items={navItems} userAccess={userAccess ?? []} />
                <NavSecondary items={secondaryItems} className="mt-auto" />
            </SidebarContent>
            <SidebarFooter>{userData && <NavUser user={userData.data} />}</SidebarFooter>
        </Sidebar>
    );
}
